import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AdminGuard } from '@/components/AdminGuard';
import { CellLangPicker } from '@/components/CellLangPicker';
import { PageError, PageLoading, Spinner } from '@/components/Spinner';
import { api, type Language, type PersonaDetail, type PersonaListItem } from '@/lib/api';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/personas')({
  component: function PersonasGuard() {
    return (
      <AdminGuard>
        <PersonasPage />
      </AdminGuard>
    );
  },
});

function PersonasPage() {
  const [cell, setCell] = useState('EI_PL_BucketX');
  const [language, setLanguage] = useState<Language>('EN');
  const [expanded, setExpanded] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['personas', cell, language],
    queryFn: () => api.listPersonas(cell, language),
    retry: 0,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1>Personas</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Operator-authored test personas. Click a row to expand and edit. Saves archive the
          previous <code className="font-mono text-xs">personas.json</code>.
        </p>
      </div>

      <CellLangPicker
        cell={cell}
        language={language}
        onChange={({ cell, language }) => {
          setCell(cell);
          setLanguage(language);
          setExpanded(null);
        }}
      />

      {q.isLoading ? (
        <PageLoading />
      ) : q.error ? (
        <PageError
          message={`No persona file uploaded for ${cell} / ${language}. Upload at v8 (port 8890) or place personas.json in knowledge/personas/${cell}/${language}/.`}
        />
      ) : (
        <PersonaTable
          cell={cell}
          language={language}
          personas={q.data?.personas ?? []}
          total={q.data?.persona_count ?? 0}
          expanded={expanded}
          onToggle={(pid) => setExpanded((p) => (p === pid ? null : pid))}
        />
      )}
    </div>
  );
}

function PersonaTable({
  cell,
  language,
  personas,
  total,
  expanded,
  onToggle,
}: {
  cell: string;
  language: Language;
  personas: PersonaListItem[];
  total: number;
  expanded: string | null;
  onToggle: (pid: string) => void;
}) {
  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        {total} persona{total === 1 ? '' : 's'}
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="w-8" />
              <th className="px-4 py-2.5 font-medium">Persona ID</th>
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Call type</th>
              <th className="px-4 py-2.5 font-medium">Expected</th>
              <th className="px-4 py-2.5 font-medium">Tone</th>
              <th className="px-4 py-2.5 font-medium text-right">Tactics</th>
              <th className="px-4 py-2.5 font-medium text-right">watch_for</th>
            </tr>
          </thead>
          <tbody>
            {personas.map((p) => (
              <PersonaRow
                key={p.persona_id}
                cell={cell}
                language={language}
                summary={p}
                isOpen={expanded === p.persona_id}
                onToggle={() => onToggle(p.persona_id)}
              />
            ))}
            {personas.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No personas in this file.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PersonaRow({
  cell,
  language,
  summary,
  isOpen,
  onToggle,
}: {
  cell: string;
  language: Language;
  summary: PersonaListItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          'cursor-pointer border-t border-border transition-colors hover:bg-accent/40',
          isOpen && 'bg-accent/30',
        )}
      >
        <td className="px-3">
          {isOpen ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" />
          )}
        </td>
        <td className="px-4 py-2.5 font-mono text-xs text-foreground">{summary.persona_id}</td>
        <td className="px-4 py-2.5">{summary.name}</td>
        <td className="px-4 py-2.5 text-muted-foreground">{summary.call_type}</td>
        <td className="px-4 py-2.5">
          <ExpectedPill expected={summary.expected_path} />
        </td>
        <td className="px-4 py-2.5 text-muted-foreground">{summary.tone}</td>
        <td className="px-4 py-2.5 text-right tabular-nums">{summary.tactics_count}</td>
        <td className="px-4 py-2.5 text-right tabular-nums">{summary.watch_for_count}</td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={8} className="bg-muted/10 px-6 py-5">
            <PersonaEditPanel cell={cell} language={language} personaId={summary.persona_id} />
          </td>
        </tr>
      )}
    </>
  );
}

function PersonaEditPanel({
  cell,
  language,
  personaId,
}: {
  cell: string;
  language: Language;
  personaId: string;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['persona', cell, language, personaId],
    queryFn: () => api.getPersona(cell, language, personaId),
  });
  const [form, setForm] = useState<PersonaDetail | null>(null);

  useEffect(() => {
    if (q.data && form === null) setForm(q.data);
  }, [q.data, form]);

  const saveMut = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('nothing to save');
      // Send only mutable fields. id, language, bank, product, bucket are
      // immutable on the server side.
      const patch = {
        name: form.name,
        call_type: form.call_type,
        expected_path: form.expected_path,
        merged_tactics: form.merged_tactics,
        tone: form.tone,
        scenario: form.scenario,
        script: form.script,
        watch_for: form.watch_for,
        dummies: form.dummies,
      };
      return api.putPersona(cell, language, personaId, patch);
    },
    onSuccess: (saved) => {
      toast.success('Persona saved');
      setForm(saved);
      qc.invalidateQueries({ queryKey: ['personas', cell, language] });
      qc.invalidateQueries({ queryKey: ['persona', cell, language, personaId] });
    },
    onError: (e) => toast.error(String(e)),
  });

  if (q.isLoading) return <PageLoading />;
  if (q.error || !q.data || !form) return <PageError message={String(q.error)} />;

  const dirty = JSON.stringify(form) !== JSON.stringify(q.data);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name">
          <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        </Field>
        <Field label="Call type">
          <Input value={form.call_type} onChange={(v) => setForm({ ...form, call_type: v })} />
        </Field>
        <Field label="Expected path">
          <Input value={form.expected_path} onChange={(v) => setForm({ ...form, expected_path: v })} />
        </Field>
        <Field label="Tone">
          <Input value={form.tone} onChange={(v) => setForm({ ...form, tone: v })} />
        </Field>
      </div>

      <Field label="Scenario">
        <Textarea
          rows={3}
          value={form.scenario}
          onChange={(v) => setForm({ ...form, scenario: v })}
        />
      </Field>

      <Field
        label="Merged tactics (one per line)"
        help="Adversarial moves the persona blends into the call."
      >
        <Textarea
          rows={3}
          value={form.merged_tactics.join('\n')}
          onChange={(v) =>
            setForm({ ...form, merged_tactics: lineSplit(v) })
          }
        />
      </Field>

      <Field
        label="Script (one move per line)"
        help="The persona LLM improvises around these — kept off the air."
      >
        <Textarea
          rows={4}
          value={form.script.join('\n')}
          onChange={(v) => setForm({ ...form, script: lineSplit(v) })}
        />
      </Field>

      <Field
        label="watch_for (one criterion per line)"
        help="What the Judge grades the agent against, per row."
      >
        <Textarea
          rows={6}
          value={form.watch_for.join('\n')}
          onChange={(v) => setForm({ ...form, watch_for: lineSplit(v) })}
        />
      </Field>

      <Field
        label="Per-persona dummies (key=value, one per line)"
        help="Override variables for THIS persona's runs only. e.g. agent_name=Custom"
      >
        <Textarea
          rows={3}
          value={Object.entries(form.dummies)
            .map(([k, v]) => `${k}=${v}`)
            .join('\n')}
          onChange={(v) => setForm({ ...form, dummies: parseKvPairs(v) })}
        />
      </Field>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
        <button
          onClick={() => setForm(q.data)}
          disabled={!dirty}
          className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-accent disabled:opacity-50"
        >
          Reset
        </button>
        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || !dirty}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
        >
          {saveMut.isPending ? (
            <Spinner className="size-3 text-primary-foreground" />
          ) : (
            <Save className="size-3.5" />
          )}
          Save persona
        </button>
      </div>
    </div>
  );
}

function lineSplit(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseKvPairs(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const eq = line.indexOf('=');
    if (eq < 0) continue;
    const k = line.slice(0, eq).trim();
    const v = line.slice(eq + 1).trim();
    if (k) out[k] = v;
  }
  return out;
}

function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {help && <span className="text-xs text-muted-foreground">{help}</span>}
    </label>
  );
}

function Input({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-input bg-card px-3 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    />
  );
}

function Textarea({
  value,
  onChange,
  rows,
}: {
  value: string;
  onChange: (v: string) => void;
  rows: number;
}) {
  return (
    <textarea
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-input bg-card p-2 text-sm leading-relaxed shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    />
  );
}

function ExpectedPill({ expected }: { expected: string }) {
  const tones: Record<string, string> = {
    happy: 'bg-success/10 text-success border-success/30',
    partial: 'bg-status-warning text-status-warning-text border-status-warning-border',
    hostile: 'bg-status-error text-status-error-text border-status-error-border',
    refusal: 'bg-status-error text-status-error-text border-status-error-border',
    vulnerable: 'bg-status-info text-status-info-text border-status-info-border',
    hardstop: 'bg-purple-100 text-purple-700 border-purple-300',
    thirdparty: 'bg-status-neutral text-status-neutral-text border-status-neutral-border',
    identity: 'bg-status-neutral text-status-neutral-text border-status-neutral-border',
    injection: 'bg-status-neutral text-status-neutral-text border-status-neutral-border',
    dispute: 'bg-status-neutral text-status-neutral-text border-status-neutral-border',
    brokenptp: 'bg-status-warning text-status-warning-text border-status-warning-border',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        tones[expected] ?? 'bg-muted text-muted-foreground border-border',
      )}
    >
      {expected}
    </span>
  );
}
